import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useParams, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { ReportErrorPill } from "./components/ReportErrorPill";
import { NEWS_CATEGORIES } from "./lib/newsCategories";
import "./App.css";

// Route-level code splitting: each page ships its own chunk (and its heavy deps
// like recharts / html-to-image / framer-motion) so the initial load stays lean.
const Home = lazy(() => import("./pages/Home"));
const Scores = lazy(() => import("./pages/Scores"));
const Competitions = lazy(() => import("./pages/Competitions"));
const Teams = lazy(() => import("./pages/Teams"));
const CompetitionDetail = lazy(() => import("./pages/CompetitionDetail"));
const MatchDetail = lazy(() => import("./pages/MatchDetail"));
const TeamProfile = lazy(() => import("./pages/TeamProfile"));
const PlayerProfile = lazy(() => import("./pages/PlayerProfile"));
const Transfers = lazy(() => import("./pages/Transfers"));
const News = lazy(() => import("./pages/News"));
const ArticleReader = lazy(() => import("./pages/ArticleReader"));
const NewsCategory = lazy(() => import("./pages/NewsCategory"));
const Compare = lazy(() => import("./pages/Compare"));
const Games = lazy(() => import("./pages/Games"));
const GameDetail = lazy(() => import("./pages/GameDetail"));
const Community = lazy(() => import("./pages/Community"));
const Forum = lazy(() => import("./pages/Forum"));
const Thread = lazy(() => import("./pages/Thread"));
const Reports = lazy(() => import("./pages/Reports"));
const Admin = lazy(() => import("./pages/Admin"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const Favorites = lazy(() => import("./pages/Favorites"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const Settings = lazy(() => import("./pages/Settings"));
const Legal = lazy(() => import("./pages/Legal"));
const NotFound = lazy(() => import("./pages/NotFound"));

function NewsSegment() {
  const { id } = useParams();
  return NEWS_CATEGORIES[id] ? <NewsCategory /> : <ArticleReader />;
}

function App() {
  useEffect(() => {
    const onCopy = (e) => {
      const sel = window.getSelection?.().toString() || "";
      if (sel.trim().length < 1) return;
      const attributed = `${sel.trim()}\n\n(Source: ${window.location.href})`;
      e.clipboardData.setData("text/plain", attributed);
      e.preventDefault();
    };
    document.addEventListener("copy", onCopy);
    return () => document.removeEventListener("copy", onCopy);
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="flex min-h-screen flex-col bg-[#F8F9FA]">
          <Navbar />
          <main className="flex min-w-0 flex-1 flex-col [&>*]:w-full [&>*]:min-w-0">
            <Suspense fallback={<div className="flex flex-1 items-center justify-center py-24" aria-busy="true"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-ink" /></div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/matches" element={<Scores />} />
              <Route path="/live" element={<Navigate to="/matches" replace />} />
              <Route path="/scores" element={<Navigate to="/matches" replace />} />
              <Route path="/update-password" element={<UpdatePassword />} />
              <Route path="/competitions" element={<Competitions />} />
              <Route path="/teams" element={<Teams />} />
              <Route path="/competition/:code" element={<CompetitionDetail />} />
              <Route path="/match/:id" element={<MatchDetail />} />
              <Route path="/team/:id" element={<TeamProfile />} />
              <Route path="/player/:id" element={<PlayerProfile />} />
              <Route path="/transfers" element={<Transfers />} />
              <Route path="/news" element={<News />} />
              <Route path="/news/league/:id" element={<NewsCategory />} />
              <Route path="/news/:id" element={<NewsSegment />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/compare/:pair" element={<Compare />} />
              <Route path="/games" element={<Games />} />
              <Route path="/games/:slug" element={<GameDetail />} />
              <Route path="/community" element={<Community />} />
              <Route path="/community/reports" element={<Reports />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/community/f/:slug" element={<Forum />} />
              <Route path="/community/t/:id" element={<Thread />} />
              <Route path="/search" element={<SearchResults />} />
              <Route path="/favorites" element={<Favorites />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/privacy" element={<Legal doc="privacy" />} />
              <Route path="/terms" element={<Legal doc="terms" />} />
              <Route path="/cookies" element={<Legal doc="cookies" />} />
              <Route path="/disclaimer" element={<Legal doc="disclaimer" />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </main>
          <Footer />
          <ReportErrorPill />
        </div>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
